import { useEffect, useState } from "react";
import { ReportOnePager, reportStyles } from "./MarketReport";

/**
 * Publicly shareable market report at /report/:share_id
 * No auth required — reps hand this URL directly to clients.
 * Tracks opens + engagement (time on page, scroll depth) so the rep
 * knows exactly when the client viewed it.
 */

const API = process.env.REACT_APP_BACKEND_URL;

const newSessionId = () => {
  try {
    if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  } catch { /* fall through */ }
  return `${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
};

const PublicMarketReport = ({ shareId }) => {
  const [state, setState] = useState({ loading: true, report: null, record: null, error: "" });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${API}/api/sales/market-report/share/${shareId}`);
        const d = await r.json();
        if (cancelled) return;
        if (!r.ok) {
          setState({ loading: false, report: null, record: null, error: d.detail || "Report not found." });
          return;
        }
        setState({
          loading: false,
          report: d.report,
          record: {
            business_name: d.business_name,
            created_at: d.created_at,
            share_id: d.share_id,
            prepared_by: d.prepared_by,
          },
          error: "",
        });
      } catch (e) {
        if (!cancelled) setState({ loading: false, report: null, record: null, error: e.message || "Network error." });
      }
    })();
    return () => { cancelled = true; };
  }, [shareId]);

  // ─── View tracking: open + heartbeats + scroll depth ───
  useEffect(() => {
    if (!state.report) return;
    let sid;
    try {
      sid = sessionStorage.getItem(`mr_sess_${shareId}`);
      if (!sid) {
        sid = newSessionId();
        sessionStorage.setItem(`mr_sess_${shareId}`, sid);
      }
    } catch {
      sid = newSessionId();
    }
    const trackUrl = `${API}/api/sales/market-report/share/${shareId}/track`;
    const start = Date.now();
    let maxScroll = 0;

    const measure = () => {
      const doc = document.documentElement;
      const total = doc.scrollHeight - window.innerHeight;
      const pct = total <= 0 ? 100 : Math.min(100, Math.round((window.scrollY / total) * 100));
      if (pct > maxScroll) maxScroll = pct;
    };

    const send = (event, useBeacon = false) => {
      const payload = JSON.stringify({
        event,
        session_id: sid,
        seconds: Math.round((Date.now() - start) / 1000),
        max_scroll_pct: maxScroll,
        referrer: document.referrer || "",
      });
      if (useBeacon && navigator.sendBeacon) {
        navigator.sendBeacon(trackUrl, new Blob([payload], { type: "application/json" }));
      } else {
        fetch(trackUrl, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: payload,
          keepalive: true,
        }).catch(() => {});
      }
    };

    send("open");
    measure();
    window.addEventListener("scroll", measure, { passive: true });
    const hb = setInterval(() => { measure(); send("heartbeat"); }, 10000);
    const onVis = () => { if (document.visibilityState === "hidden") send("final", true); };
    document.addEventListener("visibilitychange", onVis);

    return () => {
      clearInterval(hb);
      window.removeEventListener("scroll", measure);
      document.removeEventListener("visibilitychange", onVis);
      send("final", true);
    };
  }, [state.report, shareId]);

  if (state.loading) {
    return (
      <div className="pub-shell">
        <style>{reportStyles}</style>
        <style>{extraStyles}</style>
        <div className="pub-empty">
          <div className="mr-spinner big" />
          <div className="mr-empty-title">Loading your Market Report…</div>
        </div>
      </div>
    );
  }

  if (state.error || !state.report) {
    return (
      <div className="pub-shell">
        <style>{reportStyles}</style>
        <style>{extraStyles}</style>
        <div className="pub-empty">
          <div className="mr-empty-icon">◆</div>
          <div className="mr-empty-title">This report link is no longer available.</div>
          <div className="mr-empty-sub">{state.error || "Ask the person who sent you this link for a fresh copy."}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="pub-shell" data-testid="public-market-report">
      <style>{reportStyles}</style>
      <style>{extraStyles}</style>
      <div className="pub-brandbar">
        <img src="/skyeai_head.png" alt="Skye AI" className="pub-brand-logo" />
        <div className="pub-brand-text">
          <div className="pub-brand-eyebrow">A Skye AI Market Domination Report</div>
          <div className="pub-brand-prep">Prepared by Skye AI Team</div>
        </div>
      </div>
      <div className="pub-body">
        <ReportOnePager
          report={state.report}
          record={state.record}
          hidePrivateActions
        />
      </div>
    </div>
  );
};

const extraStyles = `
html, body { background: #050507; }
.pub-shell {
  min-height: 100vh;
  background: #050507;
  color: #fff;
  font-family: 'Manrope', 'Inter', -apple-system, system-ui, sans-serif;
  overflow-x: hidden;
}
.pub-brandbar {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 28px;
  border-bottom: 1px solid rgba(255,255,255,0.07);
  background: #050507;
}
.pub-brand-logo { width: 28px; height: 28px; object-fit: contain; filter: drop-shadow(0 2px 6px rgba(244,114,182,0.5)); }
.pub-brand-text { line-height: 1.2; }
.pub-brand-eyebrow {
  font-family: 'IBM Plex Mono', monospace;
  font-size: 11px;
  letter-spacing: 0.15em;
  text-transform: uppercase;
  background: linear-gradient(135deg, #f472b6, #22d3ee);
  -webkit-background-clip: text; background-clip: text; color: transparent;
  font-weight: 700;
}
.pub-brand-prep {
  font-size: 10.5px;
  color: rgba(255,255,255,0.45);
  margin-top: 2px;
}
.pub-body {
  display: flex;
  justify-content: center;
  padding: 12px 20px 70px;
}
.pub-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 70vh;
  gap: 14px;
  padding: 40px;
  text-align: center;
}
@media (max-width: 760px) {
  .pub-body { padding: 4px 14px 50px; }
}
@media print {
  .pub-brandbar { display: none !important; }
  .pub-shell { background: #fff !important; color: #111 !important; }
  .pub-body { padding: 0 !important; }
}
`;

export default PublicMarketReport;
